import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import { useFlag } from '@rsos/flags';
import { PreconfiguredMessageInterface } from '@rsos/reduxDataStore/src/features/alerts/chat.types';
import {
  useGetConversation,
  useHasConversationBeenInitiated,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { setChatVisibility } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { RootState } from '../../../../app';
import isTextBasedSource from '../../../../utils/isSourceSMS';
import AllMessages from '../../../PreconfiguredMessages/AllMessages';
import { PRECONFIG_REQUEST_LOCATION } from '../../../PreconfiguredMessages/AllMessages/utils';
import ConversationsChat from './ConversationsChat';
import ConversationsChatHeader from './ConversationsChatHeader';
import {
  AbsoluteAllMessagesContainer,
  ConversationsContainer,
} from './ConversationsChatbox.styles';

const ConversationsChatBox = () => {
  const dispatch = useDispatch();
  const callerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );

  const selectedCall = useSelectedCall();
  const mostRecentLocation = getMostRecentLoc(selectedCall);

  const hasRequestLocationAccess = useFlag('rsp_request_location');

  const hasRequestLocation = Boolean(
    hasRequestLocationAccess && isTextBasedSource(mostRecentLocation?.source),
  );

  const mapAccessToDynamicMessage: Record<string, boolean> = {
    [PRECONFIG_REQUEST_LOCATION]: hasRequestLocation,
  };

  const preconfiguredMessages = useSelector(
    (state: RootState) => state.preconfiguredMessages?.messages,
  );
  const enabledPreconfiguredMessages = preconfiguredMessages?.filter(
    message => {
      const accessCheck = mapAccessToDynamicMessage[message.title];

      if (accessCheck === undefined) {
        return message.enabled;
      }

      return accessCheck && message.enabled;
    },
  );

  const isConversationStarted = useHasConversationBeenInitiated(callerID);
  const conversation = useGetConversation(callerID);
  const [isAllMessagesVisible, setIsAllMessagesVisible] = useState(false);
  const [selectedPreconfiguredMessage, setSelectedPreconfiguredMessage] =
    useState<PreconfiguredMessageInterface | null>(null);

  // Use Redux state for chat visibility to maintain state across tab navigation
  // Force collapse if conversation timed out (was never accepted)
  const isChatBoxVisible = useSelector(
    (state: any) =>
      state.eventPane?.chatVisibility?.isConversationsChatVisible ??
      isConversationStarted,
  );

  const handleViewClose = () => {
    setIsAllMessagesVisible(false);
  };

  const handleCopyToChat = (selectedMessage: PreconfiguredMessageInterface) => {
    setIsAllMessagesVisible(false);
    setSelectedPreconfiguredMessage(selectedMessage);
  };

  const setIsChatBoxVisible = useCallback(
    (isVisible: boolean) => {
      dispatch(
        setChatVisibility({
          chatType: 'isConversationsChatVisible',
          isVisible,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (
      conversation?.source === SOURCE_TCAPI &&
      conversation?.data?.acceptStatus === 'timed_out'
    ) {
      dispatch(
        setChatVisibility({
          chatType: 'isConversationsChatVisible',
          isVisible: false,
        }),
      );
    }
  }, [dispatch, conversation]);

  return (
    <ConversationsContainer
      data-name="conversations-container"
      flex={isChatBoxVisible ? '1' : '0'}
    >
      <ConversationsChatHeader
        isChatBoxVisible={isChatBoxVisible}
        setIsChatBoxVisible={setIsChatBoxVisible}
        isConversationStarted={isConversationStarted}
      />
      {isConversationStarted && (
        <ConversationsChat
          isChatBoxVisible={isChatBoxVisible}
          setIsAllMessagesVisible={setIsAllMessagesVisible}
          selectedPreconfiguredMessage={selectedPreconfiguredMessage}
          setSelectedPreconfiguredMessage={setSelectedPreconfiguredMessage}
          hasRequestLocationAccess={hasRequestLocation}
          preconfiguredMessages={enabledPreconfiguredMessages}
        />
      )}
      {isAllMessagesVisible && (
        <AbsoluteAllMessagesContainer>
          <AllMessages
            preconfiguredMessages={enabledPreconfiguredMessages}
            onClose={handleViewClose}
            onCopyToChat={handleCopyToChat}
          />
        </AbsoluteAllMessagesContainer>
      )}
    </ConversationsContainer>
  );
};

export default ConversationsChatBox;
